import TuneIcon from "@mui/icons-material/Tune";
import { Box, ButtonBase, type SxProps, type Theme } from "@mui/material";
import type { FC } from "react";
import { BADGE, CONTROL_HEIGHT_PX, FONT_SIZE, ICON_SIZE, INK, NEUTRAL, SURFACE } from "@/theme/tokens";
import { TOUCH_TARGET_SX } from "../touchTarget";

type Props = {
	/**
	 * Already translated, and the pill's accessible name: "Filter", or the scope it is holding ("Nurse · 3 units",
	 * "Filtered"). There is deliberately no separate `aria-label` — a fixed one would hide that scope from a screen reader.
	 */
	label: string;
	/** Something is narrowed: the pill fills with the brand, so the scope reads as "on" at a glance. */
	isActive: boolean;
	onClick: () => void;
	/** Shows the scope but will not open it — the same pill gone quiet, so the scope it holds stays legible. */
	disabled?: boolean;
	/**
	 * Opt-in: the pill reaches `TOUCH_TARGET_MIN` on coarse pointers (`TOUCH_TARGET_SX`), for a phone screen's chrome.
	 * Absent, the pill keeps its `CONTROL_HEIGHT.COMPACT` box everywhere — `min-height` clamps `height`, so baking this
	 * into the base would grow every future pill on a touch laptop too (`touchTarget.ts`).
	 */
	touchTarget?: boolean;
	dataTestId: string;
	/** Placement only (`flex`, a `maxWidth` for a long scope label). */
	sx?: SxProps<Theme>;
};

/**
 * The compact pill that opens a list's filter and names the scope it holds — the funnel glyph and a label, outlined at
 * rest and filled with the brand once anything is narrowed.
 *
 * For a phone's pinned chrome, where a labelled `ToolbarTriggerButton` is too tall and a bare icon would not say
 * whether a filter is on: `CONTROL_HEIGHT.COMPACT`, fully rounded ends, and — asked for with `touchTarget`, as
 * `SystemChips` and `SystemButton` ask — a finger-sized hit area. A long label ellipsizes inside whatever width the
 * caller allows it.
 */
// DEFAULT: `disabled` absent is an enabled pill, and `touchTarget` absent keeps the pill's own density — the ordinary
// cases, not missing values.
export const FilterPillButton: FC<Props> = ({ label, isActive, onClick, disabled = false, touchTarget = false, dataTestId, sx }) => (
	<ButtonBase
		data-testid={dataTestId}
		onClick={onClick}
		disabled={disabled}
		sx={[
			{
				flex: "none",
				gap: 0.625,
				px: 1.125,
				height: CONTROL_HEIGHT_PX.COMPACT,
				borderRadius: `${BADGE.PILL_RADIUS_FULL}px`,
				border: "1px solid",
				borderColor: isActive ? "primary.main" : NEUTRAL[200],
				bgcolor: isActive ? "primary.main" : "transparent",
				color: isActive ? SURFACE : INK.SECONDARY,
				"&.Mui-disabled": { borderColor: NEUTRAL[200], bgcolor: "transparent", color: INK.DISABLED },
				fontSize: FONT_SIZE.LABEL_SM,
				fontWeight: 600,
			},
			...(touchTarget ? [TOUCH_TARGET_SX] : []),
			...(Array.isArray(sx) ? sx : [sx]),
		]}
	>
		<TuneIcon sx={{ fontSize: ICON_SIZE.PILL, flex: "none" }} />
		<Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
			{label}
		</Box>
	</ButtonBase>
);
